import { useState, useEffect, useRef, useCallback } from 'react';

export type UpdateState = 'idle' | 'available' | 'updating' | 'updated';

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const MAX_STALE_MS = 4 * 60 * 60 * 1000; // 4 hours

interface UseUpdateDetectionReturn {
  updateState: UpdateState;
  isUpdateAvailable: boolean;
  triggerUpdate: () => void;
  markUpdateAvailable: () => void;
}

export function useUpdateDetection(
  registrationRef: React.MutableRefObject<ServiceWorkerRegistration | null>
): UseUpdateDetectionReturn {
  const [updateState, setUpdateState] = useState<UpdateState>('idle');
  const lastForceUpdateRef = useRef<number>(Date.now()); // Fix: was 0

  // ---- Stable callbacks ----

  const markUpdateAvailable = useCallback(() => {
    setUpdateState((prev) => (prev === 'idle' ? 'available' : prev));
  }, []);

  const triggerUpdate = useCallback(async () => {
    try {
      setUpdateState('updating');
      let reg: ServiceWorkerRegistration | null | undefined =
        registrationRef.current;
      if (!reg) {
        reg = await navigator.serviceWorker.getRegistration();
      }
      if (!reg) {
        console.warn('[PWA] triggerUpdate: no registration found');
        setUpdateState('idle');
        return;
      }
      await reg.update();
      // Small delay helps waiting stabilize
      await new Promise((resolve) => setTimeout(resolve, 300));
      // Get freshest registration snapshot
      const freshReg =
        registrationRef.current ||
        (await navigator.serviceWorker.getRegistration());

      if (!freshReg) {
        console.warn('[PWA] triggerUpdate: registration missing after update');
        setUpdateState('idle');
        return;
      }
      // waiting is preferred
      let targetWorker = freshReg.waiting;
      // Fallback: installing worker that is already installed
      if (
        !targetWorker &&
        freshReg.installing &&
        freshReg.installing.state === 'installed'
      ) {
        targetWorker = freshReg.installing;
      }

      if (!targetWorker) {
        console.warn('[PWA] triggerUpdate: no waiting worker available');
        setUpdateState('idle');
        return;
      }

      console.log('[PWA] Triggering update');
      targetWorker.postMessage({ type: 'SKIP_WAITING' });
    } catch (e) {
      console.error('[PWA] triggerUpdate failed', e);
      setUpdateState('idle');
    }
  }, [registrationRef]);

  // ---- Single check function used by all triggers ----

  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const reg =
        registrationRef.current ||
        (await navigator.serviceWorker.getRegistration());
      if (!reg) return;
      await reg.update();

      // allow lifecycle to settle
      await new Promise((resolve) => setTimeout(resolve, 300));

      const freshReg =
        registrationRef.current ||
        (await navigator.serviceWorker.getRegistration());

      if (!freshReg?.waiting) return;
      const isOverdue = Date.now() - lastForceUpdateRef.current > MAX_STALE_MS;

      if (isOverdue) {
        console.log('[PWA] Force updating — stale threshold exceeded');
        lastForceUpdateRef.current = Date.now();
        freshReg.waiting.postMessage({
          type: 'SKIP_WAITING',
        });
      } else {
        setUpdateState('available');
      }
    } finally {
      checkingRef.current = false;
    }
  }, [registrationRef]);

  // ---- Effect 2: controllerchange → reload ----

  useEffect(() => {
    const handler = () => {
      console.log('[PWA] Controller changed → reloading');
      setUpdateState('updated');
      localStorage.setItem('offline-status', 'ready');
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handler);
    };
  }, []);

  // ---- Effect 3: polling ----

  useEffect(() => {
    const id = setInterval(checkForUpdate, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [checkForUpdate]);

  // ---- Effect 4: window focus ----

  useEffect(() => {
    window.addEventListener('focus', checkForUpdate);
    return () => window.removeEventListener('focus', checkForUpdate);
  }, [checkForUpdate]);

  // ---- Effect 5: tab visibility (check only, no silent force) ----

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [checkForUpdate]);

  return {
    updateState,
    isUpdateAvailable: updateState === 'available',
    triggerUpdate,
    markUpdateAvailable,
  };
}
